import { Play } from "lucide-react";

import type { Mission } from "@/entities/missions";

type Props = {
  mission: Mission;
  /** Human-facing mission name (registry ``display_name`` in real flows). */
  displayName: string;
  /** Short stage description ("Review · 12 of 17"). */
  stageDetail: string;
  /** Resume handler. */
  onResume: () => void;
  /** Optional last-updated relative label ("2 minutes ago"). */
  updatedLabel?: string;
  testIdRoot?: string;
};

/**
 * Reusable dashboard resume tile.
 *
 * Wired into the E2E demo route in 37.1; Executive Home mounts it in
 * 37.2 once real missions can populate the resume list.
 */
export function MissionResumeTile({
  mission,
  displayName,
  stageDetail,
  onResume,
  updatedLabel,
  testIdRoot = "mission-resume-tile",
}: Props) {
  const { current, total } = mission.progress;
  const remaining = total > 0 ? Math.max(0, total - current) : null;
  return (
    <article
      className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[color:var(--brand-charcoal,#12151C)] p-4 md:p-5"
      data-testid={testIdRoot}
      data-mission-id={mission.id}
      data-mission-type={mission.mission_type}
    >
      <div className="min-w-0 flex-1">
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80"
          data-testid={`${testIdRoot}-eyebrow`}
        >
          Continue mission
        </div>
        <h3
          className="mt-1 text-base font-semibold text-white truncate"
          data-testid={`${testIdRoot}-name`}
        >
          {displayName}
        </h3>
        <div
          className="mt-1 text-xs text-white/60 truncate"
          data-testid={`${testIdRoot}-stage`}
        >
          {stageDetail}
          {remaining !== null && remaining > 0 ? (
            <> · {remaining} remaining</>
          ) : null}
          {updatedLabel ? <> · Updated {updatedLabel}</> : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onResume}
        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-4 py-2 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition"
        data-testid={`${testIdRoot}-resume-cta`}
        aria-label={`Resume ${displayName}`}
      >
        <Play className="h-3.5 w-3.5" aria-hidden />
        Resume
      </button>
    </article>
  );
}
